import type { MapBounds } from '../types';

/**
 * MapBoundingBox Value Object
 *
 * Domain concept: "which rectangle of the map was on screen when this search
 * was saved?"
 *
 * A saved search has to record where it applies. On an area page that is the
 * area slug, which is exact. Off an area page there is no slug, so the map's
 * current rectangle is the only thing describing the search's location, and it
 * is stored as the `bbox` filter param.
 *
 * Business rules:
 * - The serialized form is `sw_lat,sw_lng,ne_lat,ne_lng`: southwest corner
 *   first, and latitude before longitude. That is the order client-app parses
 *   (`REGEXP_G_LAT_LNG_BOUNDS` in geos-extensions), and it is the reverse of
 *   GeoJSON, which is longitude first.
 * - Coordinates are rounded to six decimal places, roughly 11cm. Map viewports
 *   produce long floats and the extra digits mean nothing for a search area.
 * - Bounds that are not four finite numbers produce no bounding box. A saved
 *   search with a malformed location is worse than one with none, because
 *   client-app would filter on the bad rectangle rather than reject it.
 *
 * Immutable. Construct via `MapBoundingBox.fromBounds`.
 */

/** Decimal places kept when serializing. Six is roughly 11cm. */
const COORDINATE_PRECISION = 6;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function round(value: number): number {
  return Number(value.toFixed(COORDINATE_PRECISION));
}

export class MapBoundingBox {
  readonly #north: number;
  readonly #south: number;
  readonly #east: number;
  readonly #west: number;

  private constructor(bounds: MapBounds) {
    this.#north = round(bounds.north);
    this.#south = round(bounds.south);
    this.#east = round(bounds.east);
    this.#west = round(bounds.west);
  }

  /**
   * Builds a bounding box from map bounds, or returns null when the bounds are
   * unusable (absent, or any corner not a finite number).
   */
  static fromBounds(bounds: MapBounds | null | undefined): MapBoundingBox | null {
    if (!bounds) {
      return null;
    }
    const corners = [bounds.north, bounds.south, bounds.east, bounds.west];
    if (!corners.every(isFiniteNumber)) {
      return null;
    }
    return new MapBoundingBox(bounds);
  }

  /**
   * Reads a `bbox` param back into a bounding box, the inverse of
   * {@link toParam}. Null when the string is not four finite numbers, for the
   * same reason `fromBounds` rejects bad bounds: a malformed rectangle is worse
   * than none, since it would be searched rather than ignored.
   */
  static fromParam(param: string | null | undefined): MapBoundingBox | null {
    const parts = param?.split(',').map(Number);
    if (!parts || parts.length !== 4 || !parts.every(isFiniteNumber)) {
      return null;
    }
    const [south, west, north, east] = parts;
    return new MapBoundingBox({ north, south, east, west });
  }

  get north(): number {
    return this.#north;
  }

  get south(): number {
    return this.#south;
  }

  get east(): number {
    return this.#east;
  }

  get west(): number {
    return this.#west;
  }

  /** The `bbox` filter param: southwest corner first, latitude before longitude. */
  toParam(): string {
    return `${this.#south},${this.#west},${this.#north},${this.#east}`;
  }

  equals(other: MapBoundingBox): boolean {
    return this.toParam() === other.toParam();
  }
}
